import { UserAvatar } from "@components/Avatar";
import { useLogout } from "@features/auth";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import {
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAuthStore } from "@stores/authStore";
import { useNavigate } from "@tanstack/react-router";
import { type MouseEvent, type ReactNode, useState } from "react";

/**
 * User affordance for the sidebar footer. Opens a menu upward — anchoring
 * downward from the bottom of the sidebar would clip off-screen.
 */
export function SidebarUser() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const displayName = user.name || user.email;

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-label="Open user menu"
        sx={(t) => ({
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          px: 1,
          py: 0.75,
          borderRadius: 1,
          border: "1px solid",
          borderColor: !!anchor
            ? alpha(t.palette.primary.main, 0.45)
            : t.palette.border.default,
          bgcolor: !!anchor
            ? alpha(t.palette.primary.main, 0.06)
            : t.palette.surface.raised,
          textAlign: "left",
          position: "relative",
          transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "inherit",
            boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, !!anchor ? 0.18 : 0.04)}`,
            transition: `box-shadow ${t.motion.fast}`,
          },
          "&:hover, &.Mui-focusVisible": {
            borderColor: alpha(t.palette.primary.main, 0.3),
            bgcolor: alpha(t.palette.primary.main, 0.05),
          },
          "&:hover::before, &.Mui-focusVisible::before": {
            boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.16)}`,
          },
        })}
      >
        <UserAvatar name={user.name} picture={user.picture} size={26} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </Typography>
          {user.name && user.email && user.email !== user.name && (
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: "text.secondary",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user.email}
            </Typography>
          )}
        </Box>
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: (t) => ({
              width: 260,
              maxWidth: "calc(100vw - 16px)",
              mb: 0.5,
              borderRadius: 1.25,
              bgcolor: "surface.raised",
              border: "1px solid",
              borderColor: "border.default",
              overflow: "hidden",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: "inherit",
                boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.12)}, 0 0 24px -10px ${alpha(t.palette.primary.main, 0.4)}`,
              },
              "& .MuiList-root": {
                py: 0.5,
              },
            }),
          },
        }}
      >
        <SectionLabel label="Account" />
        <Box sx={{ px: 1.5, pb: 0.75 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: "text.primary",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.email}
          </Typography>
        </Box>
        <SectionDivider />
        <EditorialItem
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          primary="Profile"
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/profile" });
          }}
        />
        <EditorialItem
          icon={<BusinessIcon sx={{ fontSize: 16 }} />}
          primary="Organisations"
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/organisations" });
          }}
        />
        <EditorialItem
          icon={<CreditCardIcon sx={{ fontSize: 16 }} />}
          primary="Billing"
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/billing" });
          }}
        />
        <EditorialItem
          icon={<KeyIcon sx={{ fontSize: 16 }} />}
          primary="API keys"
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/api-keys" });
          }}
        />
        {user.is_admin && (
          <EditorialItem
            icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
            primary="Admin"
            secondary="Internal tools"
            onClick={() => {
              handleClose();
              void navigate({ to: "/admin/users" });
            }}
          />
        )}
        <SectionDivider />
        <EditorialItem
          icon={<LogoutIcon sx={{ fontSize: 16 }} />}
          primary="Sign out"
          danger
          onClick={() => {
            handleClose();
            logout.mutate();
          }}
        />
      </Menu>
    </>
  );
}

/** Mono ALL-CAPS section header — same kicker vocabulary as the
 *  sidebar clusters, project-card eyebrow, and dialog overline. */
function SectionLabel({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        fontFamily: (t) => t.fonts.mono,
        fontSize: "0.625rem",
        fontWeight: 500,
        color: "text.secondary",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        px: 1.5,
        pt: 0.75,
        pb: 0.25,
      }}
    >
      {label}
    </Typography>
  );
}

/** Trace-cyan tinted divider matching the workspace dropdown. */
function SectionDivider() {
  return (
    <Divider
      sx={(t) => ({
        my: 0.5,
        borderColor: alpha(t.palette.primary.main, 0.12),
      })}
    />
  );
}

interface EditorialItemProps {
  icon: ReactNode;
  primary: string;
  secondary?: string;
  /** Apply the destructive accent (used by Sign out). */
  danger?: boolean;
  onClick: () => void;
}

/**
 * Account-menu item that wears the editorial vocabulary. Hover
 * picks up the trace-cyan inset rim + soft fill matching SidebarNav
 * and the workspace dropdown. The `danger` flag flips the hover
 * accent to the error tone so Sign out reads as destructive.
 */
function EditorialItem({
  icon,
  primary,
  secondary,
  danger,
  onClick,
}: EditorialItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      disableRipple
      sx={(t) => {
        const tone = danger ? t.palette.error.main : t.palette.primary.main;
        const restColor = danger ? t.palette.error.main : t.palette.text.primary;
        return {
          mx: 0.5,
          my: 0.25,
          px: 1.25,
          py: 0.875,
          borderRadius: 0.875,
          position: "relative",
          overflow: "hidden",
          color: restColor,
          transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "inherit",
            boxShadow: "none",
            transition: `box-shadow ${t.motion.fast}`,
          },
          "&:hover": {
            bgcolor: alpha(tone, 0.08),
            color: danger ? t.palette.error.main : t.palette.primary.light,
          },
          "&:hover::before": {
            boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.22)}`,
          },
        };
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: 1,
          color: danger ? "error.main" : "text.secondary",
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={primary}
        secondary={secondary}
        sx={{ minWidth: 0 }}
        primaryTypographyProps={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          letterSpacing: "-0.005em",
          noWrap: true,
        }}
        secondaryTypographyProps={{
          noWrap: true,
          sx: {
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.65rem",
            letterSpacing: "0.04em",
            color: "text.secondary",
            mt: 0.125,
          },
        }}
      />
    </MenuItem>
  );
}
